/**
 * Sale offers applied by staff for walk-in / guest customers (Centre Manager
 * walk-in form, Sales guest booking form) and the desk credit sale.
 *
 * The backend decides what is actually granted (bookings guest-multi with
 * apply_sale_offer, POST /payments/desk-credit-sale); these pieces only show
 * the staff member what the customer will get and whose account it goes to.
 */
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Loader2, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../context/AuthContext';
import {
  usePromotion, offerLive, dayPassFreeQuantity, meetingRoomFreeHours,
  DAY_PASS_OFFER, MEETING_ROOM_OFFER, CREDITS_2X_OFFER, MEETING_ROOM_TYPES,
} from '../lib/promotions';

export const rewardDateLabel = (iso) => (iso ? format(new Date(iso), 'd MMM yyyy') : '');

/** True while a booking offer staff can apply (Day Pass / Meeting Room) is live. */
export const walkInBookingOfferLive = () => offerLive(DAY_PASS_OFFER) || offerLive(MEETING_ROOM_OFFER);

// Whose account a walk-in sale offer would credit — GET /promotions/offer-customer,
// debounced while staff type the email. Nothing is created by the lookup.
export const useOfferCustomer = (email) => {
  const { api } = useAuth();
  const [state, setState] = useState({ loading: false, data: null });
  const trimmed = (email || '').trim();
  const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  useEffect(() => {
    if (!looksValid) { setState({ loading: false, data: null }); return undefined; }
    let cancelled = false;
    setState(s => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      api.get('/promotions/offer-customer', { params: { email: trimmed } })
        .then(r => { if (!cancelled) setState({ loading: false, data: r.data }); })
        .catch(() => { if (!cancelled) setState({ loading: false, data: null }); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [api, trimmed, looksValid]);
  return { ...state, looksValid };
};

// What the account lookup means for the staff member, in one line.
export const OfferAccountStatus = ({ email, lookup, what = 'rewards' }) => {
  if (!lookup.looksValid) {
    return <p className="text-xs text-amber-700 dark:text-amber-300">{email ? 'Enter a valid email address.' : `Enter the customer's email. The ${what} go to their Clayworks account.`}</p>;
  }
  if (lookup.loading || !lookup.data) return <p className="text-xs text-muted-foreground">Checking account…</p>;
  const d = lookup.data;
  if (!d.eligible) return <p className="text-xs text-red-600" data-testid="offer-account-problem">{d.reason}</p>;
  if (d.has_account) return <p className="text-xs text-green-700 dark:text-green-400" data-testid="offer-account-existing">✓ Existing customer account{d.name ? `: ${d.name}` : ''}. The {what} will be added to it.</p>;
  return <p className="text-xs text-muted-foreground" data-testid="offer-account-new">No account yet. One will be created for this email, and the customer will get an email to set their password.</p>;
};

/**
 * Sale reward one slot earns — mirrors backend _quote_walk_in_reward.
 * `bookable` is the selected space, `passQty` the Day Pass quantity, `hours`
 * the booked hours. Returns { ok, text } or null when there's nothing to say yet.
 */
export const offerPreviewFor = ({ bookable, passQty = 1, hours = 0 }, promo) => {
  if (!bookable || !promo) return null;
  const mode = bookable.booking_mode || 'hourly';
  if (bookable.bookable_type === 'day_pass' && mode === 'daily' && offerLive(DAY_PASS_OFFER)) {
    const free = dayPassFreeQuantity(passQty);
    const min = promo.offers[DAY_PASS_OFFER].block || 3;
    return free
      ? { ok: true, text: `Earns ${free} free Day Pass${free !== 1 ? 'es' : ''}, usable after the paid pass${passQty !== 1 ? 'es' : ''}` }
      : { ok: false, text: `Add ${min - passQty} more pass${min - passQty !== 1 ? 'es' : ''} (${min} or more) to earn as many free Day Passes` };
  }
  if (mode === 'hourly' && MEETING_ROOM_TYPES.includes(bookable.bookable_type) && offerLive(MEETING_ROOM_OFFER)) {
    if (!hours) return null;
    const free = meetingRoomFreeHours(hours);
    const block = promo.offers[MEETING_ROOM_OFFER].block || 2;
    return free
      ? { ok: true, text: `Earns ${free} free hrs as Meeting Room Credits` }
      : { ok: false, text: `Book ${block} or more hours to earn ${block} free hours` };
  }
  return { ok: false, text: 'This space isn\'t part of the sale offer' };
};

export const OfferPreviewLine = ({ preview, testId }) => {
  if (!preview) return null;
  return (
    <div className={`p-2 rounded text-xs ${preview.ok ? 'bg-amber-500/15 text-amber-900 dark:text-amber-200' : 'bg-muted text-muted-foreground'}`} data-testid={testId}>
      {preview.ok ? '🎁 ' : ''}{preview.text}
    </div>
  );
};

/**
 * "Apply the sale offer" switch with the offer summary and the account check.
 * Renders nothing unless a booking offer is live. `extraNote` = form-specific
 * rule shown with the summary (e.g. partial payment being turned off).
 */
export const WalkInOfferPanel = ({ promo, checked, onCheckedChange, email, lookup, extraNote = '' }) => {
  if (!walkInBookingOfferLive()) return null;
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${checked ? 'border-amber-500/60 bg-amber-500/10' : 'border-amber-500/30 bg-amber-500/5'}`} data-testid="walkin-sale-offer">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">🪔 Apply the {promo?.name || 'sale'} offer</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Same as online:
            {offerLive(DAY_PASS_OFFER) && ` Day Pass Buy X, Get X Free (${promo.offers[DAY_PASS_OFFER].block || 3}+ passes).`}
            {offerLive(MEETING_ROOM_OFFER) && ` Meeting rooms +${promo.offers[MEETING_ROOM_OFFER].block || 2} free hrs (${promo.offers[MEETING_ROOM_OFFER].block || 2}+ hrs).`}
            {' '}Rewards are added to the customer's account once paid (cash or link) and are valid till {rewardDateLabel(promo?.reward_valid_until)}. Manual discounts are off with the offer.{extraNote ? ` ${extraNote}` : ''}
          </p>
        </div>
        <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid="walkin-apply-offer-switch" />
      </div>
      {checked && <OfferAccountStatus email={email} lookup={lookup} />}
    </div>
  );
};

/** Payment-link dialog block listing what the offer will add once paid. */
export const OfferRewardsNotice = ({ saleOffer }) => {
  if (!saleOffer?.rewards?.length) return null;
  return (
    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40" data-testid="payment-link-offer">
      <p className="text-sm text-muted-foreground mb-1">🪔 Sale offer, added to the customer's account once paid</p>
      {saleOffer.rewards.map((r, i) => (
        <p key={r.booking_id || i} className="text-sm font-medium">{r.summary}{r.bookable_name ? ` · ${r.bookable_name}` : ''}</p>
      ))}
    </div>
  );
};

/** Toast / dialog text for a guest-multi response that applied the offer. */
export const offerResultNotes = (saleOffer) => ({
  account: saleOffer?.account_created ? ` New account created for ${saleOffer.customer_email}; set-password email sent.` : '',
  earned: saleOffer?.rewards?.length ? ` Offer applied: ${saleOffer.rewards.map(r => r.summary).join(', ')} added to ${saleOffer.customer_email}.` : '',
});

const EMPTY_CREDIT_SALE = { customer_name: '', customer_email: '', customer_phone: '', credits: '', payment_method: 'razorpay', payment_reference: '' };

// Sell a credit tier to a walk-in at the online price, with the sale bonus
// (e.g. 2X credits) while it runs — POST /payments/desk-credit-sale.
// `onLinkSent(result)` shows the payment link; cash sales just close.
export const SellCreditsModal = ({ open, onClose, onLinkSent, theme = {} }) => {
  const { api } = useAuth();
  const promo = usePromotion();
  const [form, setForm] = useState(EMPTY_CREDIT_SALE);
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const lookup = useOfferCustomer(open ? form.customer_email : '');

  useEffect(() => {
    if (!open || tiers.length) return;
    api.get('/credits/tiers').then(r => setTiers(r.data.tiers || [])).catch(() => toast.error('Could not load credit tiers'));
  }, [open, api, tiers.length]);

  const tier = tiers.find(t => String(t.credits) === String(form.credits));
  const bonusLive = offerLive(CREDITS_2X_OFFER);
  const multiplier = bonusLive ? (promo.offers[CREDITS_2X_OFFER].multiplier || 1) : 1;
  const bonusFor = (credits) => Math.max(0, Math.round(credits * (multiplier - 1)));
  const close = () => { setForm(EMPTY_CREDIT_SALE); onClose(false); };

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    if (!lookup.looksValid) { toast.error("Customer email is required. The credits go to their account."); return; }
    if (lookup.data && !lookup.data.eligible) { toast.error(lookup.data.reason); return; }
    if (!tier) { toast.error('Choose a credit pack'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/payments/desk-credit-sale', {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim() || null,
        credits: tier.credits,
        payment_method: form.payment_method,
        payment_reference: form.payment_reference.trim() || null,
      });
      const accountNote = data.account_created ? ' New account created; set-password email sent.' : '';
      if (data.payment_link) {
        onLinkSent({
          message: data.message + accountNote,
          payment_link: data.payment_link,
          total_amount: data.total_amount,
          guest_name: data.customer_name || form.customer_name,
          guest_email: data.customer_email,
          guest_phone: form.customer_phone,
          sale_offer: data.bonus_credits ? { rewards: [{ summary: `${data.bonus_credits} bonus credits (${promo?.name || 'sale'} 2X)` }] } : null,
        });
      } else {
        toast.success(data.message + accountNote);
      }
      close();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sell credits');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className={`${theme.cardBg || ''} ${theme.cardBorder || ''} max-w-md max-h-[85vh] overflow-y-auto`}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Coins className="w-5 h-5 text-primary" />Sell Credits to a Walk-in</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          {bonusLive && (
            <div className="p-3 rounded-lg border border-amber-500/50 bg-amber-500/10 text-xs" data-testid="sell-credits-offer">
              🪔 <strong>{promo.name}: {multiplier}X credits</strong>, same price as online. Bonus credits never expire.
            </div>
          )}
          <div className="space-y-2">
            <Label>Customer Name *</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={theme.inputBg} placeholder="Enter customer name" data-testid="sell-credits-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className={theme.inputBg} placeholder="customer@email.com" data-testid="sell-credits-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className={theme.inputBg} placeholder="+91 98765 43210" />
            </div>
          </div>
          <OfferAccountStatus email={form.customer_email} lookup={lookup} what="credits" />
          <div className="space-y-2">
            <Label>Credit Pack *</Label>
            <Select value={String(form.credits)} onValueChange={(v) => setForm({ ...form, credits: v })}>
              <SelectTrigger className={theme.inputBg} data-testid="sell-credits-tier"><SelectValue placeholder="Choose a pack" /></SelectTrigger>
              <SelectContent>
                {tiers.map(t => (
                  <SelectItem key={t.credits} value={String(t.credits)}>
                    {t.credits} credits{bonusFor(t.credits) ? ` + ${bonusFor(t.credits)} FREE` : ''} · ₹{Math.round(t.total_amount).toLocaleString('en-IN')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {tier && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{tier.credits} credits ({tier.label}{tier.discount_percent ? `, ${tier.discount_percent}% off` : ''})</span><span>₹{tier.discounted_amount.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span>₹{tier.gst_amount.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold pt-1.5 border-t border-primary/10"><span>Total</span><span className="text-primary">₹{tier.total_amount.toFixed(2)}</span></div>
              {bonusFor(tier.credits) > 0 && (
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300 pt-1">🎁 Customer gets {tier.credits + bonusFor(tier.credits)} credits ({tier.credits} + {bonusFor(tier.credits)} free)</p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger className={theme.inputBg}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="razorpay">Razorpay (Send Payment Link)</SelectItem>
                <SelectItem value="cash">Cash / UPI at the desk</SelectItem>
              </SelectContent>
            </Select>
            {form.payment_method === 'cash' && (
              <Input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} className={theme.inputBg} placeholder="Receipt / UPI reference (optional)" data-testid="sell-credits-reference" />
            )}
            <p className="text-xs text-muted-foreground">
              {form.payment_method === 'cash' ? 'Credits are added as soon as you confirm. Only confirm once the money has been received.' : 'Credits are added once the customer pays the link.'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} className={theme.cardBorder}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="sell-credits-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Coins className="w-4 h-4 mr-2" />}
            {form.payment_method === 'cash' ? 'Confirm Cash Sale' : 'Send Payment Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
